import { Editor } from "@tinymce/tinymce-react";
import { useAuth } from "@/auth/hooks/useAuth";
import useImageCompressor from "@/hooks/useImageCompressor";
import usePostMultipart from "@/hooks/usePostMultipart";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import tinymce from "tinymce";
import "tinymce/tinymce";
import "tinymce/icons/default";
import "tinymce/models/dom";
import "tinymce/plugins/directionality";
import "tinymce/plugins/image";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/preview";
import "tinymce/plugins/save";
import "tinymce/plugins/table";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/themes/silver";
import { useEffect, useState } from "react";
import type { Editor as TinyMceEditor } from "tinymce";
import { useTranslation } from "react-i18next";

type EditorIntroProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  uiContainerSelector?: string;
  height?: number;
  onReady?: (editor: TinyMceEditor) => void;
  enableDocumentLinkUpload?: boolean;
};

type FolderNode = {
  id: number;
  defaultKey?: string | null;
  children?: FolderNode[];
};

type DocumentFolderTreeResponse = {
  porteur?: Record<string, FolderNode[]>;
};

type PendingLinkInsertion = {
  editor: TinyMceEditor;
  fileUrl: string;
  defaultText: string;
};

const PRESENTATION_DOCUMENT_TYPE_ID = 2;
const PROJECTS_ROOT_DEFAULT_KEY = "projects-root";
const LINKABLE_FILE_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp";

const escapeHtml = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const findFolderByDefaultKey = (
  folders: FolderNode[] = [],
  defaultKey: string,
): FolderNode | null => {
  for (const folder of folders) {
    if ((folder.defaultKey ?? null) === defaultKey) {
      return folder;
    }

    const nested = findFolderByDefaultKey(folder.children ?? [], defaultKey);
    if (nested) {
      return nested;
    }
  }

  return null;
};

const EditorIntro = ({
  value,
  onChange,
  disabled = false,
  uiContainerSelector,
  height = 400,
  onReady,
  enableDocumentLinkUpload = false,
}: EditorIntroProps) => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const { postMultipart } = usePostMultipart<FormData>();
  const { handleCompressImage } = useImageCompressor();
  const [pendingLinkInsertion, setPendingLinkInsertion] =
    useState<PendingLinkInsertion | null>(null);
  const [pendingLinkLabel, setPendingLinkLabel] = useState("");
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const stopFocusTrapConflict = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.closest(
          ".tox-tinymce-aux, .tox-silver-sink, .moxman-window, .tam-assetmanager-root",
        )
      ) {
        event.stopImmediatePropagation();
      }
    };

    const stopPointerTrapConflict = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.closest(
          ".tox-tinymce-aux, .tox-silver-sink, .tox-dialog, .tox-dialog-wrap, .tox-menu, .tox-collection, .tox-pop, .moxman-window, .tam-assetmanager-root",
        )
      ) {
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener("focusin", stopFocusTrapConflict, true);
    document.addEventListener("pointerdown", stopPointerTrapConflict, true);
    return () => {
      document.removeEventListener("focusin", stopFocusTrapConflict, true);
      document.removeEventListener("pointerdown", stopPointerTrapConflict, true);
    };
  }, []);

  const fetchAuthenticatedJson = async (url: string, init?: RequestInit) => {
    const token =
      localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

    const response = await fetch(url, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers ?? {}),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  };

  const resolveCarrierOwnerId = async (): Promise<number | null> => {
    const authUserId = Number(user?.id ?? 0) || null;
    if (!authUserId) return null;

    const carrierTree = await fetchAuthenticatedJson(
      `${import.meta.env.VITE_API_ENDPOINT}/tree/carrier`,
      { method: "GET" },
    );

    const carriers = Array.isArray(carrierTree) ? carrierTree : [];
    if (carriers.some((carrier: any) => Number(carrier?.id) === authUserId)) {
      return authUserId;
    }

    return Number(carriers[0]?.id ?? 0) || authUserId;
  };

  const ensureProjectsRootFolderId = async (): Promise<number | null> => {
    const ownerId = await resolveCarrierOwnerId();
    if (!ownerId) return null;

    const tree = (await fetchAuthenticatedJson(
      `${import.meta.env.VITE_API_ENDPOINT}/document-folders/tree`,
      { method: "GET" },
    )) as DocumentFolderTreeResponse;

    const scopedFolders = tree?.porteur?.[String(ownerId)] ?? [];
    const existingFolder = findFolderByDefaultKey(
      scopedFolders,
      PROJECTS_ROOT_DEFAULT_KEY,
    );
    if (existingFolder?.id) {
      return Number(existingFolder.id);
    }

    const savedTree = (await fetchAuthenticatedJson(
      `${import.meta.env.VITE_API_ENDPOINT}/document-folders/save`,
      {
        method: "POST",
        body: JSON.stringify({
          scopeType: "porteur",
          scopeId: ownerId,
          defaultKey: PROJECTS_ROOT_DEFAULT_KEY,
          reuseByDefaultKey: true,
          name: "Mes projets",
        }),
      },
    )) as DocumentFolderTreeResponse;

    await fetchAuthenticatedJson(
      `${import.meta.env.VITE_API_ENDPOINT}/document-folders/default/hide`,
      {
        method: "POST",
        body: JSON.stringify({
          scopeType: "porteur",
          scopeId: ownerId,
          defaultKey: PROJECTS_ROOT_DEFAULT_KEY,
        }),
      },
    );

    const savedFolder = findFolderByDefaultKey(
      savedTree?.porteur?.[String(ownerId)] ?? [],
      PROJECTS_ROOT_DEFAULT_KEY,
    );

    return Number(savedFolder?.id ?? 0) || null;
  };

  const handleInsertUploadedDocumentLink = async (editor: TinyMceEditor) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = LINKABLE_FILE_ACCEPT;

    input.addEventListener("change", async (event: Event) => {
      const target = event.target as HTMLInputElement | null;
      const file = target?.files?.[0];
      if (!file) return;

      editor.setProgressState(true);

      try {
        setUploadErrorMessage(null);
        const folderId = await ensureProjectsRootFolderId();
        if (!folderId) {
          throw new Error("Impossible de préparer le dossier Mes projets.");
        }

        const data = new FormData();
        data.append("type", "2");
        data.append("subType", String(PRESENTATION_DOCUMENT_TYPE_ID));
        data.append("description", "Lien ajouté depuis l'introduction");

        let uploadFile: File | Blob = file;
        if (file.type.startsWith("image/")) {
          const compressedBlob = await handleCompressImage(file, 1600, 1600, 0.9);
          if (compressedBlob) {
            uploadFile = new File([compressedBlob], file.name, {
              type: compressedBlob.type || file.type,
            });
          }
        }

        data.append("files[]", uploadFile);
        const uploaded = await postMultipart({
          url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
          data,
        });

        if (!Array.isArray(uploaded) || !uploaded[0]?.id) {
          throw new Error(uploaded?.message || "Impossible d'importer le fichier.");
        }

        await fetchAuthenticatedJson(
          `${import.meta.env.VITE_API_ENDPOINT}/document-folders/attach-media`,
          {
            method: "POST",
            body: JSON.stringify({
              folderId,
              mediaIds: uploaded.map((media: any) => media?.id).filter(Boolean),
            }),
          },
        );

        const fileUrl = String(uploaded[0]?.uri ?? "").startsWith("http")
          ? uploaded[0].uri
          : `${import.meta.env.VITE_MEDIA_BASE}${uploaded[0]?.uri ?? ""}`;
        if (!fileUrl) {
          throw new Error("Lien du fichier introuvable.");
        }

        const suggestedText = file.name.replace(/\.[^.]+$/, "") || file.name;
        const selectedText = editor.selection.getContent({ format: "text" }).trim();
        setPendingLinkLabel(selectedText || suggestedText);
        setPendingLinkInsertion({
          editor,
          fileUrl,
          defaultText: suggestedText,
        });
      } catch (error: any) {
        setUploadErrorMessage(
          error?.message || "Une erreur est survenue lors de l'ajout du lien.",
        );
      } finally {
        editor.setProgressState(false);
      }
    });

    input.click();
  };

  return (
    <div>
      <Dialog
        open={Boolean(pendingLinkInsertion)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingLinkInsertion(null);
            setPendingLinkLabel("");
          }
        }}
      >
        <DialogContent className="min-w-[min(92vw,520px)] max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Insérer un lien vers le fichier</DialogTitle>
            <DialogDescription>
              Choisissez le texte cliquable à afficher dans l’introduction.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Texte du lien
            </label>
            <Input
              value={pendingLinkLabel}
              onChange={(event) => setPendingLinkLabel(event.target.value)}
              placeholder="Ex. Voir le visuel"
              autoFocus
              onKeyDown={(event) => {
                if (event.key === "Enter" && pendingLinkInsertion) {
                  event.preventDefault();
                  const finalLinkText =
                    pendingLinkLabel.trim() || pendingLinkInsertion.defaultText;
                  pendingLinkInsertion.editor.insertContent(
                    `<a href="${escapeHtml(pendingLinkInsertion.fileUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(finalLinkText)}</a>`,
                  );
                  setPendingLinkInsertion(null);
                  setPendingLinkLabel("");
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setPendingLinkInsertion(null);
                setPendingLinkLabel("");
              }}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (!pendingLinkInsertion) return;
                const finalLinkText =
                  pendingLinkLabel.trim() || pendingLinkInsertion.defaultText;
                pendingLinkInsertion.editor.insertContent(
                  `<a href="${escapeHtml(pendingLinkInsertion.fileUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(finalLinkText)}</a>`,
                );
                setPendingLinkInsertion(null);
                setPendingLinkLabel("");
              }}
            >
              Insérer le lien
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {uploadErrorMessage && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {uploadErrorMessage}
        </div>
      )}
      <style>
        {`
          .tox-tinymce-aux,
          .tox-silver-sink,
          .tox.tox-tinymce-aux,
          .tox-dialog,
          .tox-dialog-wrap,
          .tox-menu,
          .tox-collection,
          .tox-tbtn--select,
          .tox-pop {
            z-index: 20000 !important;
            pointer-events: auto !important;
          }
        `}
      </style>
      <Editor
        licenseKey="gpl"
        value={value}
        onEditorChange={(newValue) => onChange(newValue)}
        onInit={(_, editor) => onReady?.(editor)}
        init={{
          zindex: 20000,
          ui_container: uiContainerSelector,
          skin: false,
          skin_url: "/assets/tinymce/skins/ui/oxide",
          content_css: "/assets/tinymce/skins/content/default/content.css",
          language: i18n.language === "fr" ? "fr_FR" : "en_CA",
          paste_data_images: true,
          convert_urls: false,
          height,
          menubar: false,
          placeholder: "Ecrivez ici ...",
          branding: false,
          plugins: [
            "lists",
            "link",
            "table",
            "directionality",
            "preview",
            "save",
            "image",
          ],
          content_style: `
  .img-center { display:block; margin-left:auto; margin-right:auto; max-width:100%; height:auto; }
  .img-left { float:left; margin:0 1em 1em 0; max-width:50%; height:auto; }
  .img-right { float:right; margin:0 0 1em 1em; max-width:50%; height:auto; }
  img { max-width:100%; height:auto; }
`,
          formats: {
            aligncenter: [
              { selector: "p", styles: { textAlign: "center" } },
              {
                selector: "img",
                styles: {
                  display: "block !important",
                  marginLeft: "auto !important",
                  marginRight: "auto !important",
                },
              },
            ],
          },
          toolbar:
            `undo redo | bold italic underline forecolor backcolor | bullist numlist | fontsize link table ${enableDocumentLinkUpload ? "| documentlinkupload" : ""} | preview image | alignleft aligncenter alignright`,
          automatic_uploads: true,
          image_dimensions: true,
          image_caption: true,
          object_resizing: true,
          file_picker_types: "image",
          table_appearance_options: true,
          file_picker_callback: (cb) => {
            const input = document.createElement("input");
            input.setAttribute("type", "file");
            input.setAttribute("accept", "image/*");

            input.addEventListener("change", (e: any) => {
              const file = e?.target?.files?.[0];
              if (!file) return;

              const reader = new FileReader();
              reader.addEventListener("load", () => {
                const id = `blobid${new Date().getTime()}`;
                const blobCache = tinymce.activeEditor?.editorUpload.blobCache;
                const base64 = `${reader?.result ?? ""}`.split(",")[1];
                const blobInfo = blobCache?.create(id, file, base64);
                blobCache?.add(blobInfo);
                cb(blobInfo.blobUri(), { title: file.name });
              });
              reader.readAsDataURL(file);
            });

            input.click();
          },
          draggable_modal: true,
          setup: (editor) => {
            if (enableDocumentLinkUpload) {
              editor.ui.registry.addButton("documentlinkupload", {
                text: "Fichier lié",
                tooltip: "Importer un fichier et l'insérer comme lien",
                onAction: () => {
                  void handleInsertUploadedDocumentLink(editor);
                },
              });
            }

            editor.on("NodeChange", (e) => {
              e.element.querySelectorAll?.("img").forEach((img: HTMLImageElement) => {
                const parent = img.closest("p");
                if (parent?.style.textAlign === "center" || parent?.getAttribute("align") === "center") {
                  img.style.display = "block";
                  img.style.marginLeft = "auto";
                  img.style.marginRight = "auto";
                  img.style.maxWidth = "100%";
                  img.style.height = "auto";
                }
              });
            });
          },
        }}
        disabled={disabled}
      />
    </div>
  );
};

export default EditorIntro;
